import { useEffect, useCallback } from "react";

/**
 * Check if running in Capacitor native app
 */
const isCapacitorNative = (): boolean => {
  return typeof (window as any).Capacitor !== 'undefined' && 
         (window as any).Capacitor.isNativePlatform?.() === true;
};

/**
 * Requests geolocation permission on app launch.
 * Stores coordinates in sessionStorage for use across the app.
 * Shows a friendly toast if permission is denied.
 */
export const useGlobalGeolocation = () => {
  const hydrateFromCache = useCallback(() => {
    try {
      const existing = sessionStorage.getItem("user_location");
      if (existing) return;

      const raw = localStorage.getItem("berastays_location_cache");
      if (!raw) return;
      const data = JSON.parse(raw);
      if (!data || typeof data.lat !== "number" || typeof data.lng !== "number") return;

      sessionStorage.setItem(
        "user_location",
        JSON.stringify({ latitude: data.lat, longitude: data.lng, timestamp: data.timestamp || Date.now() })
      );
    } catch {
      return;
    }
  }, []);

  useEffect(() => {
    if (isCapacitorNative()) return;
    hydrateFromCache();
  }, [hydrateFromCache]);
};

/** Helper to read the cached user location */
export const getUserLocation = (): { latitude: number; longitude: number } | null => {
  try {
    const rawSession = sessionStorage.getItem("user_location");
    if (rawSession) {
      const data = JSON.parse(rawSession);
      if (data?.timestamp && Date.now() - data.timestamp > 30 * 24 * 60 * 60 * 1000) {
        sessionStorage.removeItem("user_location");
      } else if (typeof data?.latitude === "number" && typeof data?.longitude === "number") {
        return { latitude: data.latitude, longitude: data.longitude };
      }
    }

    const rawLocal = localStorage.getItem("berastays_location_cache");
    if (!rawLocal) return null;
    const localData = JSON.parse(rawLocal);
    if (localData?.timestamp && Date.now() - localData.timestamp > 30 * 24 * 60 * 60 * 1000) {
      return null;
    }
    if (typeof localData?.lat === "number" && typeof localData?.lng === "number") {
      return { latitude: localData.lat, longitude: localData.lng };
    }

    return null;
  } catch {
    return null;
  }
};
